/**
 * The route patterns the tracker reports as `screen`.
 *
 * A *pattern*, never a resolved path. `/news/rytmus` and `/news/pretisy` are the same
 * screen; recording them separately would give every article its own screen and make
 * the dimension useless — the slug already travels on the event as `subject_key`.
 *
 * Declared as data, the same way `components/shell/navigation.ts` declares the tab
 * bar, so adding a route is one line here and nothing else.
 */

import { matchPath } from "react-router-dom";

import { ACCOUNT_DELETION_PATH } from "~/lib/account-deletion";
import { LEGAL_ROUTE_PATH } from "~/lib/legal";
import { WELCOME_PATH } from "~/lib/onboarding";
import { ROUTINE_PATH } from "~/lib/routine";

/**
 * Most specific first: `matchPath` is exact, but ordering keeps the intent obvious
 * and survives someone adding a looser pattern later.
 */
export const SCREEN_PATTERNS: readonly string[] = [
  "/news/:slug",
  "/news",
  "/events/:slug",
  "/events",
  "/protocols",
  ROUTINE_PATH,
  "/notifications",
  "/highlights/:slug",
  "/profile/email",
  "/profile/username",
  "/profile/notifications",
  "/profile/reminders",
  "/profile/language",
  "/profile/appearance",
  "/profile",
  "/membership",
  "/paywall",
  "/invite/:token",
  WELCOME_PATH,
  LEGAL_ROUTE_PATH,
  ACCOUNT_DELETION_PATH,
];

/**
 * The pattern a pathname belongs to, or null when nothing matches.
 *
 * Null rather than the raw pathname on purpose: an unmatched path is either a
 * redirect in flight or a route nobody added here, and inventing a screen name for
 * it would quietly grow the dimension without anyone deciding to.
 */
export function screenPatternFor(pathname: string): string | null {
  for (const pattern of SCREEN_PATTERNS) {
    if (matchPath({ path: pattern, end: true }, pathname)) return pattern;
  }
  return null;
}
